import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, Tabs, TabsList, TabsTrigger } from '@staff-app/ui';
import { useStaffGetCustomer } from '../hooks/useStaffCustomer';

/**
 * Container for the per-customer drill-in. Renders:
 *   - header: company name, plan badge, rollup counts
 *   - nested <Tabs> (Devices / Fleets) that syncs with URL
 *   - <Outlet /> for the selected tab page
 */
export function CustomerShell() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, loading, error } = useStaffGetCustomer(accountId);
  const customer = data?.staffGetCustomer;

  // Derive active tab from path.
  const isFleets = location.pathname.includes('/fleets');
  const activeTab = isFleets ? 'fleets' : 'devices';

  const handleTabChange = (next: string) => {
    if (!accountId) return;
    navigate(`/staff/customers/${encodeURIComponent(accountId)}/${next}`, {
      replace: true,
    });
  };

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="customer-shell">
      <Button
        variant="ghost"
        size="sm"
        className="w-fit cursor-pointer"
        onClick={() => navigate('/staff/customers')}
        aria-label={'Back to customers'}
        data-testid="customer-shell-back"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {'Back to customers'}
      </Button>

      {loading && !customer ? (
        <div className="text-sm text-muted-foreground">{'Loading customer…'}</div>
      ) : error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {'Failed to load customer.'} {error.message}
        </div>
      ) : customer ? (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-foreground" data-testid="customer-name">
                {customer.companyName}
              </h1>
              <p className="font-mono text-xs text-muted-foreground">{customer.accountId}</p>
            </div>
            {customer.plan ? (
              <span className="rounded-sm bg-secondary px-2 py-1 text-xs font-semibold uppercase tracking-wide text-secondary-foreground">
                {customer.plan}
              </span>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <RollupCard label={'Total devices'} value={customer.totalDevices} />
            <RollupCard label={'Online'} value={customer.onlineDevices} tone="text-success" />
            <RollupCard label={'Stale'} value={customer.staleDevices} tone="text-warning" />
            <RollupCard
              label={'Offline'}
              value={customer.offlineDevices}
              tone="text-destructive"
            />
            <RollupCard label={'Fleets'} value={customer.totalFleets} />
          </div>
        </>
      ) : null}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-2">
        <TabsList>
          <TabsTrigger value="devices" className="cursor-pointer" data-testid="tab-devices">
            {'Devices'}
          </TabsTrigger>
          <TabsTrigger value="fleets" className="cursor-pointer" data-testid="tab-fleets">
            {'Fleets'}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Outlet />
    </div>
  );
}

function RollupCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm" data-testid={`rollup-${label}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${tone ?? 'text-foreground'}`}>{value}</div>
    </div>
  );
}
